import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articleVideos } from '@/mocks/category';
import { categories } from '@/mocks/home';
import NotFound from '@/pages/NotFound';

function formatViews(views: number): string {
  if (views >= 10000) return (views / 10000).toFixed(1) + '万';
  return views.toString();
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = articleVideos[articleId];

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedVideos = article.relatedIds
    .map((rid) => articleVideos[rid])
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '蜜桃在线',
    },
    publisher: {
      '@type': 'Organization',
      name: '蜜桃在线',
      url: 'https://dicecho.cn',
    },
    image: article.thumbnail,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://dicecho.cn/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '蜜桃在线', item: 'https://dicecho.cn' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://dicecho.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.category, to: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
          {/* Article Content */}
          <article className="py-6 md:py-10">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {formatViews(article.views)} 次观看
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500"></i>
                  </div>
                  {article.rating} 分
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </div>
                  {article.duration}
                </span>
              </div>
            </header>
            {/* Thumbnail */}
            <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - ${article.category}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md cursor-pointer hover:bg-white/30 transition-all group">
                  <div className="w-0 h-0 border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-l-[20px] border-l-white ml-1 group-hover:scale-110 transition-transform"></div>
                </div>
              </div>
              <span className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 text-white text-sm rounded-lg">
                {article.duration}
              </span>
            </div>
            {/* Description */}
            <div className="prose prose-sm md:prose-base max-w-none mb-10">
              <div
                className="text-foreground-700 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
            {/* Tags / Meta */}
            <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">标签：</span>
              <span className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">
                {article.category}
              </span>
              <span className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">
                高清视频
              </span>
              <span className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">
                蜜桃在线
              </span>
            </div>
          </article>
          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="pb-14 md:pb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedVideos.slice(0, 3).map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}